import React, { useState, useEffect } from 'react';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import FormLabel from '@mui/material/FormLabel';
import TextField from '@mui/material/TextField';
import { useApi } from '../../../../../hooks/useApi';
import { useCompanyDispatch, useCompanyState } from '../../../../../stateProviders/CompanyStateProvider';
import { ActionButtons } from '../../../../../globalComponents/modal/ActionButtons';
import { Modal } from '../../../../../globalComponents/modal/Modal';
import { DesignPackage } from '../../../../../types/api/LogisticCosts';

export const EditDesignPackageModal: React.FC<{
    rowData: DesignPackage;
    gridRef: React.MutableRefObject<any>;
    modalOpen: boolean;
    setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({ rowData, gridRef, modalOpen, setModalOpen }) => {
    const api = useApi();
    const companyState = useCompanyState();
    const companyDispatch = useCompanyDispatch();
    const [pkg, setPkg] = useState({
        packageName: '',
        packagePrice: '',
        noPermitDiscount: '',
    });

    useEffect(() => {
        setPkg({
            packageName: rowData.packageName || '',
            packagePrice: String(rowData.packagePrice ?? ''),
            noPermitDiscount: String(rowData.noPermitDiscount ?? '0'),
        });
    }, [rowData]);

    const price = Number(pkg.packagePrice);
    const discount = Number(pkg.noPermitDiscount);
    const isValidPrice = pkg.packagePrice !== '' && !isNaN(price) && price > 0;
    const isValidDiscount = !isNaN(discount) && discount >= 0 && discount <= price;

    return (
        <Modal
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            modalTitle={
                <Typography variant="h2" fontWeight="bold">
                    Edit Design Package
                </Typography>
            }
            modalBody={
                <Stack spacing={2} sx={{ mb: 2 }}>
                    <Stack>
                        <FormLabel component="legend" sx={{ mb: 1 }} required>
                            Package Name
                        </FormLabel>
                        <TextField
                            fullWidth
                            name="packageName"
                            type="text"
                            placeholder="Example: Gold Package"
                            value={pkg.packageName}
                            onChange={(e) => setPkg({ ...pkg, packageName: e.target.value })}
                        />
                    </Stack>
                    <Stack>
                        <FormLabel component="legend" sx={{ mb: 1 }} required>
                            Package Price ($)
                        </FormLabel>
                        <TextField
                            fullWidth
                            name="packagePrice"
                            type="number"
                            value={pkg.packagePrice}
                            onChange={(e) => setPkg({ ...pkg, packagePrice: e.target.value })}
                            slotProps={{ htmlInput: { min: 0.01, step: 0.01 } }}
                        />
                    </Stack>
                    <Stack>
                        <FormLabel component="legend" sx={{ mb: 1 }}>
                            No Permit Discount ($)
                        </FormLabel>
                        <TextField
                            fullWidth
                            name="noPermitDiscount"
                            type="number"
                            value={pkg.noPermitDiscount}
                            onChange={(e) => setPkg({ ...pkg, noPermitDiscount: e.target.value })}
                            slotProps={{ htmlInput: { min: 0, step: 0.01 } }}
                            error={pkg.noPermitDiscount !== '' && !isValidDiscount}
                            helperText={
                                pkg.noPermitDiscount !== '' && !isValidDiscount
                                    ? 'Discount cannot exceed the package price or be negative'
                                    : ''
                            }
                        />
                    </Stack>
                </Stack>
            }
            modalFooter={
                <ActionButtons
                    confirmButtonText="Edit"
                    confirmCallback={async () => {
                        if (!companyState.logisticCosts?._id) return;
                        await api.logisticCosts.editDesignPackage(companyState.logisticCosts._id, {
                            packageName: pkg.packageName.trim(),
                            packagePrice: Math.round(price * 100) / 100,
                            noPermitDiscount: Math.round(discount * 100) / 100,
                            _id: rowData._id,
                        });
                        const logisticCosts = await api.logisticCosts.find({
                            _id: companyState.logisticCosts._id,
                        });
                        companyDispatch({ logisticCosts });
                        gridRef.current?.onRefresh();
                        setModalOpen(false);
                    }}
                    confirmDisabled={
                        pkg.packageName.trim() === '' ||
                        !isValidPrice ||
                        !isValidDiscount
                    }
                    confirmButtonStyle="success"
                    cancelCallback={() => setModalOpen(false)}
                />
            }
        />
    );
};
